// Portfolio Watch panel + brief export — Story #5115 Stream 7e (Task #5138).
// Backed by real, already-merged endpoints only: watch/unwatch CRUD (Task
// #5133, whitespace/portfolio_watchlist.py), the per-competitor
// encroachment-pressure counters Task #5134's rescan writes (surfaced here
// via the new GET /whitespace/portfolio_pressure route this task added,
// since no JSON route read them back before), and the .docx brief export
// (Task #5136, whitespace/portfolio_brief.py).
//
// WHAT "WATCHING A REGION" MEANS AT PORTFOLIO SCOPE (documented honestly,
// not fabricated): portfolio_watchlist.create_watch takes a `corpus` +
// cosine-similarity `band` — the SAME shape a single-project White-Space
// region already carries (types/whitespace.ts's WhitespaceRegion) — never a
// topology candidate's boundary or a synergy cell's component pair (neither
// carries a band; passing one anyway would make portfolio_rescan.py's
// annulus re-scan track the wrong thing entirely). So this panel lets the
// user pick a member project, see THAT project's already-scored regions
// (the real GET /whitespace/regions/{project_id} data OpportunityExplorer
// already renders), and promote one region to a PORTFOLIO-wide watch —
// same band + corpus, re-scanned every rescan cycle across every member
// project the portfolio actually authorizes (server re-checks every id).
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, FileDown, Loader2, TrendingUp, X } from 'lucide-react'
import {
  createPortfolioWatch,
  deletePortfolioWatch,
  getAggregatorPortfolioProjects,
  getPortfolioBrief,
  getPortfolioEntitlement,
  getPortfolioPressure,
  getWhitespaceRegions,
  listPortfolioWatches,
} from '../../services/api'
import {
  findPortfolioWatch,
  portfolioEntitlementMemberProjectIds,
  portfolioWatchesForAggregator,
  regionBandKey,
  regionCorpusLabel,
  regionLabel,
  regionWatchRecordIds,
  similarityBandRangeLabel,
  sortRegionsByScore,
  portfolioProjectNames,
} from '../../lib/whitespace'
import { useAggregator } from '../../contexts/AggregatorContext'
import type { PortfolioWatch, WhitespaceRegion } from '../../types/whitespace'
import LoadingSkeleton from '../shared/LoadingSkeleton'
import EmptyState from '../shared/EmptyState'
import HelpPopover from '../help/HelpPopover'

// Same cap portfolio_watchlist.py's own create_watch route enforces
// (MAX_PORTFOLIO_WATCH_MEMBER_PROJECTS) — sending more is pointless, the
// server would only authorize the first 50 anyway.
const MAX_WATCH_MEMBER_PROJECTS = 50

/** Decode a base64 .docx payload and trigger a real browser file download —
 * never just log success. Exported so the vitest suite can exercise it
 * directly against a mocked URL.createObjectURL/anchor click (jsdom has no
 * real download capability — see this task's own AC on that). */
export function downloadBase64File(base64: string, filename: string, contentType: string): void {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const blob = new Blob([bytes], { type: contentType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function projectLabel(projectId: string, names: Map<string, string>): string {
  return names.get(projectId) ?? projectId
}

export default function PortfolioWatchPanel() {
  const { activeAggregatorId } = useAggregator()
  const qc = useQueryClient()
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [noticeTone, setNoticeTone] = useState<'info' | 'error'>('info')

  const { data: entitlement, isLoading: isEntitlementLoading } = useQuery({
    queryKey: ['portfolio-entitlement', activeAggregatorId],
    queryFn: () => getPortfolioEntitlement(activeAggregatorId),
  })

  // Shares AggregatorPortfolio's / SynergyMatrix's exact query key — project
  // names for the picker + watch rows cost no extra fetch.
  const { data: portfolioData } = useQuery({
    queryKey: ['aggregator-portfolio', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
  })
  const names = portfolioProjectNames(portfolioData)

  // Story #5648 (A7's #5731): own and aggregator watch rows share ONE table
  // keyed owner_id/watch_id, so sending no scope is ambiguous rather than
  // neutral — listPortfolioWatches' own doc comment says the client must state
  // which set it is asking for. This panel is the AGGREGATOR-scope watch view,
  // so it says so, and its query key carries the scope: an own-scope watch
  // surface can never be served this list out of the cache, or vice versa.
  const { data: allWatches, isLoading: isWatchesLoading } = useQuery({
    queryKey: ['portfolio-watches', 'aggregator'],
    queryFn: () => listPortfolioWatches('aggregator'),
  })
  const watches = useMemo(
    () => portfolioWatchesForAggregator(allWatches ?? [], activeAggregatorId),
    [allWatches, activeAggregatorId],
  )

  const { data: pressureData, isLoading: isPressureLoading } = useQuery({
    queryKey: ['portfolio-pressure', activeAggregatorId],
    queryFn: () => getPortfolioPressure(activeAggregatorId),
  })

  const memberProjectIds = useMemo(
    () => (entitlement ? portfolioEntitlementMemberProjectIds(entitlement) : []),
    [entitlement],
  )

  const { data: regionsData, isFetching: isRegionsFetching } = useQuery({
    queryKey: ['whitespace-regions', selectedProjectId],
    queryFn: () => getWhitespaceRegions(selectedProjectId as string),
    enabled: !!selectedProjectId,
  })
  const regions = sortRegionsByScore(regionsData?.regions ?? [])

  const watchMutation = useMutation({
    mutationFn: async (region: WhitespaceRegion) => {
      const corpus = regionCorpusLabel(region)
      const existing = findPortfolioWatch(watches, corpus, region.band)
      if (existing) {
        await deletePortfolioWatch(existing.watch_id)
        return { action: 'unwatched' as const }
      }
      await createPortfolioWatch(
        {
          member_project_ids: memberProjectIds.slice(0, MAX_WATCH_MEMBER_PROJECTS),
          corpus,
          band: region.band,
          record_ids: regionWatchRecordIds(region),
          wos_score: region.wos_score,
        },
        activeAggregatorId,
      )
      return { action: 'watched' as const }
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['portfolio-watches'] })
      setNoticeTone('info')
      setNotice(result.action === 'watched' ? 'Added to your portfolio watchlist.' : 'Removed from your portfolio watchlist.')
    },
    onError: (e) => {
      setNoticeTone('error')
      setNotice((e as Error)?.message ?? 'Failed to update the portfolio watchlist.')
    },
  })

  const unwatchMutation = useMutation({
    mutationFn: (watchId: string) => deletePortfolioWatch(watchId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolio-watches'] })
      setNoticeTone('info')
      setNotice('Removed from your portfolio watchlist.')
    },
    onError: (e) => {
      setNoticeTone('error')
      setNotice((e as Error)?.message ?? 'Failed to remove the watch.')
    },
  })

  const briefMutation = useMutation({
    mutationFn: () => getPortfolioBrief(activeAggregatorId),
    onSuccess: (brief) => {
      downloadBase64File(brief.docx_base64, brief.filename, brief.content_type)
      setNoticeTone('info')
      setNotice(`Downloaded ${brief.filename}.`)
    },
    onError: (e) => {
      setNoticeTone('error')
      setNotice((e as Error)?.message ?? 'Failed to generate the portfolio brief.')
    },
  })

  const pressureCompetitors = pressureData?.competitors ?? []
  const windowDays = pressureData?.window_days ?? 90

  return (
    <div data-testid="portfolio-watch-panel">
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <h2 className="text-sm font-semibold text-iflow-900 flex items-center gap-1.5">
          <Eye className="w-4 h-4" /> Portfolio Watch
        </h2>
        <HelpPopover topic="pf-watch" size={14} label="Help: what tracking a region here means" />
        <div className="flex-1" />
        <button
          onClick={() => briefMutation.mutate()}
          disabled={briefMutation.isPending}
          data-testid="export-brief-button"
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-amber-400 hover:bg-amber-300 text-gray-900 rounded-lg transition-colors disabled:opacity-50"
        >
          {briefMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          {briefMutation.isPending ? 'Generating brief…' : 'Export portfolio brief (.docx)'}
        </button>
      </div>

      <p className="text-[11.5px] text-gray-600 mb-3">
        Watch a white-space region across your whole portfolio — every rescan cycle re-checks it against every
        member project you're currently entitled to, not just the one it was picked from.
      </p>

      {notice && (
        <div
          className={`mb-3 p-2 border text-xs rounded-lg flex items-center justify-between ${
            noticeTone === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-sky-50 border-sky-200 text-iflow-700'
          }`}
          role={noticeTone === 'error' ? 'alert' : 'status'}
        >
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Pick a region to watch — real per-project scored regions, promoted to portfolio scope on watch. */}
        <div className="light-card p-4">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Watch a region across your portfolio
          </div>
          {isEntitlementLoading ? (
            <LoadingSkeleton type="list" rows={2} />
          ) : memberProjectIds.length === 0 ? (
            <p className="text-xs text-gray-500">No entitled member projects to watch yet.</p>
          ) : (
            <>
              <select
                aria-label="Choose a project to view scored regions"
                className="w-full text-xs border border-sky-200 rounded-lg px-2 py-1.5 mb-3"
                value={selectedProjectId ?? ''}
                onChange={(e) => setSelectedProjectId(e.target.value || null)}
              >
                <option value="">Choose a project…</option>
                {memberProjectIds.map((pid) => (
                  <option key={pid} value={pid}>
                    {projectLabel(pid, names)}
                  </option>
                ))}
              </select>

              {!selectedProjectId ? (
                <p className="text-xs text-gray-500">Pick a project to see its scored white-space regions.</p>
              ) : isRegionsFetching ? (
                <LoadingSkeleton type="list" rows={3} />
              ) : regions.length === 0 ? (
                <p className="text-xs text-gray-500">No scored regions for this project yet.</p>
              ) : (
                <ul className="space-y-1.5" data-testid="watchable-region-list">
                  {regions.map((region) => {
                    const corpus = regionCorpusLabel(region)
                    const existing = findPortfolioWatch(watches, corpus, region.band)
                    const isWatched = !!existing
                    return (
                      <li
                        key={regionBandKey(region.band)}
                        className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-sky-500/5"
                      >
                        <div className="min-w-0">
                          <div className="text-[11.5px] font-semibold text-iflow-900 truncate">{regionLabel(region)}</div>
                          <div className="text-[10.5px] text-gray-500">
                            WOS {Math.round(region.wos_score)} · {corpus}
                          </div>
                        </div>
                        <button
                          onClick={() => watchMutation.mutate(region)}
                          disabled={watchMutation.isPending}
                          aria-pressed={isWatched}
                          className={`flex items-center gap-1 px-2 py-1 text-[10.5px] font-medium rounded-md transition-colors disabled:opacity-50 flex-shrink-0 ${
                            isWatched
                              ? 'bg-iflow-700 text-white hover:bg-iflow-800'
                              : 'border border-sky-200 text-gray-700 hover:bg-sky-50'
                          }`}
                        >
                          {isWatched ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          {isWatched ? 'Unwatch' : 'Watch'}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Currently-watched regions across the whole portfolio. */}
        <div className="light-card p-4">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Currently watched ({watches.length})
          </div>
          {isWatchesLoading ? (
            <LoadingSkeleton type="list" rows={2} />
          ) : watches.length === 0 ? (
            <EmptyState
              icon={<Eye className="w-6 h-6" />}
              title="No portfolio watches yet"
              description="Pick a project on the left and watch one of its scored regions to track it across your whole portfolio."
            />
          ) : (
            <ul className="space-y-1.5" data-testid="watched-region-list">
              {watches.map((watch: PortfolioWatch) => (
                <li key={watch.watch_id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-sky-500/5">
                  <div className="min-w-0">
                    <div className="text-[11.5px] font-semibold text-iflow-900 truncate">
                      {watch.corpus} · {similarityBandRangeLabel(watch.band)}
                    </div>
                    <div className="text-[10.5px] text-gray-500 truncate">
                      {watch.member_project_ids.map((pid) => projectLabel(pid, names)).join(', ')}
                      {watch.wos_score != null ? ` · WOS ${Math.round(watch.wos_score)}` : ''}
                      {' · watched since '}
                      {(watch.created_at || '').slice(0, 10)}
                    </div>
                  </div>
                  <button
                    onClick={() => unwatchMutation.mutate(watch.watch_id)}
                    disabled={unwatchMutation.isPending}
                    className="flex items-center gap-1 px-2 py-1 text-[10.5px] font-medium border border-sky-200 text-gray-700 hover:bg-sky-50 rounded-md transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    <EyeOff className="w-3 h-3" /> Unwatch
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Per-competitor encroachment-pressure counters (Task #5134's data,
          this task's first JSON route reading it back). */}
      <div className="light-card p-4 mt-4">
        <div className="flex items-center gap-1.5 mb-2">
          <TrendingUp className="w-3.5 h-3.5 text-iflow-500" />
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Competitor encroachment pressure (rolling {windowDays} days)
          </span>
        </div>
        {isPressureLoading ? (
          <LoadingSkeleton type="list" rows={2} />
        ) : pressureCompetitors.length === 0 ? (
          <p className="text-xs text-gray-500">
            No competitor encroachment recorded against this portfolio's watched regions yet — pressure counters
            update every rescan cycle as new patent filings enter a watched region.
          </p>
        ) : (
          <table className="w-full text-xs" data-testid="pressure-table">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="font-medium pb-1.5">Competitor</th>
                <th className="font-medium pb-1.5 text-right">Pressure count</th>
              </tr>
            </thead>
            <tbody>
              {pressureCompetitors.map((c) => (
                <tr key={c.competitor_norm} className="border-t border-sky-100">
                  <td className="py-1.5 text-gray-800">{c.display_name}</td>
                  <td className="py-1.5 text-right font-semibold text-iflow-900">{c.pressure_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
